import { Col, Form, Row, Space } from 'antd';
import useDebounce from 'hooks/useDebounce';
import { find, get } from 'lodash';
import { useCallback, useState } from 'react';
import { useMutation, useQuery } from 'react-query';

import { handleApiErrors } from '../../api/axiosInstance';
import FAVRCreatorAPI from '../../api/favr-creator';
import FAVRValuesAPI from '../../api/favr-values';
import InsuranceRatesAPI from '../../api/insurance-rates';
import { fetchRateDetails } from '../../api/products';
import RateQuotesAPI from '../../api/rate-quotes';
import { USER_API } from '../../api/user';
import { Button } from '../../components';
import Box from '../../components/Box';
import Switch from '../../components/Switch';
import Toast from '../../components/Toast';
import PageContainer from '../../containers/PageContainer';
import InternalLinks from '../../enum/InternalLinks';
import withPageState from '../../hocs/withPageState';
import useDidUpdateEffect from '../../hooks/useDidUpdateEffect';
import FavrRateResultsPanel from './FavrRateResultsPanel';
import {
  buildZmyrParameter,
  formatFormValuesForAPI,
  formatSaveRateData,
} from './generateFavrRates.utils';
import GenerateFavrRatesForm from './GenerateFavrRatesForm';
import classNames from './index.module.scss';

const getFirstDefined = (...values) => values.find(value => value !== undefined && value !== null);

const parsePositiveNumber = value => {
  const parsedValue = parseFloat(value);

  return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
};

const getAnnualMilesFromRetention = (retentionMiles, retentionYears) => {
  const parsedRetentionMiles = parsePositiveNumber(retentionMiles);
  const parsedRetentionYears = parsePositiveNumber(retentionYears);

  if (!parsedRetentionMiles || !parsedRetentionYears) return null;

  return Math.round(parsedRetentionMiles / parsedRetentionYears);
};

const GenerateFavrRatesView = props => {
  const { t, history } = props;
  const [form] = Form.useForm();

  const companyId = Form.useWatch(['companyId'], form);
  const productId = Form.useWatch(['productId'], form);
  const userId = Form.useWatch(['userId'], form);
  const retentionYears = Form.useWatch(['retentionYears'], form);
  const debouncedRetentionYears = useDebounce(retentionYears);
  const countryCode = Form.useWatch(['country'], form);
  const [allowanceMode, setAllowanceMode] = useState(false);
  const [favrRateResults, setFavrRateResults] = useState();
  const retentionMiles = Form.useWatch(['retentionMiles'], form);
  const debouncedRetentionMiles = useDebounce(retentionMiles);
  const derivedAnnualMiles = getAnnualMilesFromRetention(
    debouncedRetentionMiles,
    debouncedRetentionYears,
  );
  const [refreshRate, setRefreshRate] = useState(false);
  const [customAddress, setCustomAddress] = useState(null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserAddress, setNewUserAddress] = useState(null);

  const userContextSet = isNewUser ? !!newUserAddress?.postalCode : !!userId;
  const step = companyId && productId && userContextSet ? 'inputs' : 'selector';

  const productDetailsQuery = useQuery({
    enabled: !!productId,
    queryKey: ['fetchRateDetails', productId],
    queryFn: () => fetchRateDetails(productId),
    onSuccess: data => {
      const country = get(data, 'country');
      form.setFieldValue('businessUsePercentage', get(data, 'favrSettings.businessUse'));
      form.setFields([
        {
          name: 'highwayDrivingPercentage',
          value: getFirstDefined(
            get(data, 'favrSettings.highwayDrivingPercentage'),
            form.getFieldValue('highwayDrivingPercentage'),
          ),
          errors: [],
        },
      ]);
      form.setFieldValue('retentionYears', get(data, 'favrSettings.retentionYears'));

      if (['US', 'CA'].includes(country)) {
        form.setFieldValue('country', country);
      } else {
        form.resetFields(['country']);
      }
    },
  });

  const userDetailsQuery = useQuery({
    enabled: !!userId && !isNewUser,
    queryKey: ['getUserDetails', userId],
    queryFn: () => USER_API.getUserDetails(userId),
  });

  const userZipCode = isNewUser
    ? get(newUserAddress, 'postalCode')
    : customAddress
    ? get(customAddress, 'postalCode')
    : get(userDetailsQuery.data, 'personalInfor.homeAddress.postalCode');

  const userState = isNewUser
    ? get(newUserAddress, 'state')
    : customAddress
    ? get(customAddress, 'state')
    : get(userDetailsQuery.data, 'personalInfor.homeAddress.state');

  const insuranceRatesQuery = useQuery({
    enabled: step === 'inputs' && !!userState,
    queryKey: ['insuranceRates', userState],
    queryFn: () => new InsuranceRatesAPI().fetchByState(userState),
  });

  const userDetailsReady = isNewUser || userDetailsQuery.isSuccess;
  const inputsReady = step === 'inputs' && userDetailsReady && insuranceRatesQuery.isSuccess;

  const zmyr = productDetailsQuery.data
    ? buildZmyrParameter(
        productDetailsQuery.data,
        userId,
        debouncedRetentionMiles,
        debouncedRetentionYears,
        userZipCode,
      )
    : null;

  const standardVehicleId = get(productDetailsQuery.data, 'standardVehicle._id');

  const favrInputsQuery = useQuery({
    enabled: inputsReady && !!userZipCode && !!standardVehicleId && !!derivedAnnualMiles,
    queryKey: [
      'favrInputs',
      standardVehicleId,
      userZipCode,
      debouncedRetentionYears,
      debouncedRetentionMiles,
      derivedAnnualMiles,
    ],
    queryFn: () => {
      return new FAVRValuesAPI().fetchRateInputs({
        zip: userZipCode,
        annualMiles: derivedAnnualMiles,
        retentionYears: debouncedRetentionYears,
        standardVehicleId,
      });
    },
    onError: () => {},
    onSuccess: data => {
      if (!data) return;
      form.setFieldValue('fuelType', data.fuel || 'gasoline');
      form.setFieldValue('cashPrice', data.totalCashPrice);
      form.setFieldValue('tires', data.tires);
      form.setFieldValue(
        'fuelMpgCity',
        getFirstDefined(
          get(data, 'mpgConfig.cityMpg'),
          data.fuelMpgCity,
          data.fuel_mpg_city,
          data.mpgCity,
          data.mpg_city,
          get(data, 'summary.fuel_mpg_city'),
          get(data, 'mpg.city'),
          get(data, 'fuelEconomy.city'),
        ),
      );
      form.setFieldValue(
        'fuelMpgHighway',
        getFirstDefined(
          get(data, 'mpgConfig.highwayMpg'),
          data.fuelMpgHighway,
          data.fuel_mpg_highway,
          data.mpgHighway,
          data.mpg_highway,
          get(data, 'summary.fuel_mpg_highway'),
          get(data, 'mpg.highway'),
          get(data, 'fuelEconomy.highway'),
        ),
      );
      if (data.costsPerYear) {
        form.setFieldValue('costsPerYear', data.costsPerYear);
      }
    },
  });

  const favrInputsReady =
    favrInputsQuery.isSuccess && !!favrInputsQuery.data && !favrInputsQuery.isFetching;

  const generateFavrRateMutation = useMutation({
    mutationFn: values => new FAVRValuesAPI().generateFavrRate(values),
    onSuccess: data => {
      setFavrRateResults(data);
    },
    onError: e => {
      handleApiErrors(e.response, () => {
        Toast({
          type: 'error',
          message: t('generateFavrRateError'),
        });
      });
    },
  });

  const saveFavrRateMutation = useMutation({
    mutationFn: rateData => new FAVRCreatorAPI().saveRate(rateData),
    onSuccess: () => {
      Toast({
        type: 'success',
        message: t('rateSavedSuccessfully'),
      });

      const userEmail = get(userDetailsQuery.data, 'email');
      history.push(
        `${InternalLinks.FAVR_RATES}?companyId=${companyId}&searchTerm=${encodeURIComponent(
          userEmail,
        )}`,
      );
    },
    onError: e => {
      handleApiErrors(e.response, () => {
        Toast({
          type: 'error',
          message: t('saveRateError'),
        });
      });
    },
  });

  const saveDraftMutation = useMutation({
    mutationFn: draftData => RateQuotesAPI.createRateQuote(draftData),
    onSuccess: () => {
      Toast({
        type: 'success',
        message: t('rateQuoteSavedSuccessfully'),
      });
      history.push(InternalLinks.RATE_QUOTES);
    },
    onError: e => {
      handleApiErrors(e.response, () => {
        Toast({
          type: 'error',
          message: t('saveDraftError'),
        });
      });
    },
  });

  useDidUpdateEffect(() => {
    form.resetFields([
      'userType',
      'userId',
      'newUserEmail',
      'productId',
      'fixedRate',
      'variableRate',
      'country',
      'businessUsePercentage',
      'insuranceMonthly',
      'retentionYears',
      'retentionMiles',
      'costsPerYear',
      'cashPrice',
      'tires',
      'fuelPrice',
      'fuelType',
      'fuelMpgCity',
      'fuelMpgHighway',
      'highwayDrivingPercentage',
    ]);
    form.setFieldValue('userType', 'existing');
    setIsNewUser(false);
    setNewUserEmail('');
    setNewUserAddress(null);
  }, [companyId, form]);

  useDidUpdateEffect(() => {
    if (productDetailsQuery.data) {
      const data = productDetailsQuery.data;
      const favrSettings = get(data, 'favrSettings', {});
      const customSettings = get(favrSettings, 'customSettings', []);
      const userCustomSettings = find(customSettings, { userId });

      if (userCustomSettings) {
        const retentionMiles =
          get(favrSettings, 'retentionYears', 1) *
          get(userCustomSettings, 'annualBusinessMiles', 0);
        if (retentionMiles > 0) {
          form.setFieldValue('retentionMiles', retentionMiles);
        }
      } else {
        const retentionMiles =
          get(favrSettings, 'retentionYears', 1) * get(favrSettings, 'annualBusinessMiles', 0);
        if (retentionMiles > 0) {
          form.setFieldValue('retentionMiles', retentionMiles);
        }
      }
    }
  }, [productDetailsQuery.data, userId, form]);

  useDidUpdateEffect(() => {
    if (!debouncedRetentionYears || debouncedRetentionYears < 1) {
      form.setFieldsValue({ costsPerYear: [] });
      return;
    }

    const DEBOUNCE_DELAY = 400;

    // Sync costsPerYear array length with retentionYears
    const timeoutId = setTimeout(() => {
      const currentCostsPerYear = form.getFieldValue('costsPerYear') || [];
      const targetLength = parseInt(debouncedRetentionYears, 10);

      if (currentCostsPerYear.length === targetLength) {
        return;
      }

      let updatedCostsPerYear = [...currentCostsPerYear];

      if (currentCostsPerYear.length < targetLength) {
        const rowsToAdd = targetLength - currentCostsPerYear.length;
        for (let i = 0; i < rowsToAdd; i++) {
          updatedCostsPerYear.push({
            repairs: undefined,
            maintenance: undefined,
            taxesAndFees: undefined,
            depreciation: undefined,
          });
        }
      } else {
        updatedCostsPerYear = updatedCostsPerYear.slice(0, targetLength);
      }

      form.setFieldsValue({ costsPerYear: updatedCostsPerYear });
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(timeoutId);
  }, [debouncedRetentionYears, form]);

  useDidUpdateEffect(() => {
    setCustomAddress(null);
  }, [userId]);

  const handleAllowanceModeChange = useCallback(
    checked => {
      setAllowanceMode(checked);
      setFavrRateResults(undefined);

      if (!checked) {
        form.resetFields(['fixedRate']);
      }
    },
    [form],
  );

  const handleFormValuesChange = useCallback(() => {
    setFavrRateResults();
  }, []);

  const handleFormSubmit = useCallback(() => {
    if (!favrInputsReady) return;

    form.validateFields().then(validatedValues => {
      const formattedValues = formatFormValuesForAPI(validatedValues, allowanceMode);

      if (isNewUser && newUserAddress) {
        formattedValues.postalCode = newUserAddress.postalCode;
        formattedValues.state = newUserAddress.state;
        formattedValues.city = newUserAddress.city;
        formattedValues.streetOne = newUserAddress.streetOne;
      }

      return generateFavrRateMutation.mutate(formattedValues);
    });
  }, [form, allowanceMode, generateFavrRateMutation, isNewUser, newUserAddress, favrInputsReady]);

  const handleSaveRate = useCallback(
    (isDraft = false) => {
      const formValues = form.getFieldsValue();
      const saveData = formatSaveRateData(
        formValues,
        productDetailsQuery.data,
        isNewUser ? null : userDetailsQuery.data,
        favrRateResults,
        zmyr,
        allowanceMode,
        refreshRate,
        isNewUser ? newUserAddress : customAddress,
        isNewUser ? newUserEmail : null,
      );
      if (isDraft) {
        const { rates, ...restSaveData } = saveData;
        const draftPayload = { ...restSaveData, ...rates };
        saveDraftMutation.mutate(draftPayload);
      } else {
        saveFavrRateMutation.mutate(saveData);
      }
    },
    [
      form,
      productDetailsQuery.data,
      userDetailsQuery.data,
      favrRateResults,
      zmyr,
      allowanceMode,
      refreshRate,
      customAddress,
      saveFavrRateMutation,
      saveDraftMutation,
      isNewUser,
      newUserEmail,
      newUserAddress,
    ],
  );

  const handleCustomAddressChange = useCallback(address => {
    setCustomAddress(address);
  }, []);

  const handleNewUserModeChange = useCallback(
    value => {
      const isNew = value === 'new';
      setIsNewUser(isNew);

      if (isNew) {
        form.resetFields(['userId']);
        setCustomAddress(null);
        setNewUserAddress({ country: 'US' });
      } else {
        form.resetFields(['newUserEmail']);
        setNewUserEmail('');
        setNewUserAddress(null);
      }

      setFavrRateResults(undefined);
    },
    [form],
  );

  const handleNewUserEmailChange = useCallback(email => {
    setNewUserEmail(email);
  }, []);

  const handleNewUserAddressChange = useCallback(address => {
    setNewUserAddress(address);
  }, []);

  return (
    <PageContainer
      title={t('generateFavrRate')}
      headerContentClassName={classNames.headerWrapper}
      contentClassName={classNames.pageContentWrapper}
    >
      <Space size="middle" direction="vertical">
        <Row gutter={[8, 8]}>
          <Col xs={24} lg={16}>
            <Box>
              <GenerateFavrRatesForm
                t={t}
                form={form}
                step={step}
                allowanceMode={allowanceMode}
                companyId={companyId}
                userId={userId}
                productId={productId}
                retentionYears={retentionYears}
                inputsReady={inputsReady}
                userZipCode={userZipCode}
                userState={userState}
                insuranceRatesData={insuranceRatesQuery.data}
                userDetails={userDetailsQuery.data}
                productDetails={productDetailsQuery.data}
                isLoading={generateFavrRateMutation.isLoading}
                isLoadingUserDetails={userDetailsQuery.isLoading}
                isLoadingProductDetails={productDetailsQuery.isLoading}
                favrInputsLoading={favrInputsQuery.isFetching}
                favrInputsError={favrInputsQuery.error}
                favrInputsReady={favrInputsReady}
                onRetryFavrInputs={favrInputsQuery.refetch}
                onAllowanceModeChange={handleAllowanceModeChange}
                onValuesChange={handleFormValuesChange}
                onFinish={handleFormSubmit}
                onCustomAddressChange={handleCustomAddressChange}
                isNewUser={isNewUser}
                onNewUserModeChange={handleNewUserModeChange}
                onNewUserEmailChange={handleNewUserEmailChange}
                onNewUserAddressChange={handleNewUserAddressChange}
                newUserAddress={newUserAddress}
              />
            </Box>
          </Col>
        </Row>

        <Row>
          {favrRateResults && !!favrRateResults?.fixedRate && !!favrRateResults?.variableRate && (
            <Col xs={24} lg={16}>
              <Box>
                <Space direction="vertical" size="large">
                  <FavrRateResultsPanel t={t} countryCode={countryCode} results={favrRateResults} />

                  <Row justify="end" align="middle" gutter={16}>
                    <Col>
                      <Switch
                        checked={refreshRate}
                        onChange={setRefreshRate}
                        label={t('refreshRate?')}
                      />
                    </Col>
                    <Col>
                      <Button
                        type="secondary"
                        onClick={() => handleSaveRate(true)}
                        loading={saveDraftMutation.isLoading}
                        disabled={
                          saveDraftMutation.isLoading ||
                          saveFavrRateMutation.isLoading ||
                          !favrRateResults
                        }
                      >
                        {t('saveDraft')}
                      </Button>
                    </Col>
                    <Col>
                      <Button
                        onClick={() => handleSaveRate()}
                        loading={saveFavrRateMutation.isLoading}
                        disabled={
                          saveFavrRateMutation.isLoading ||
                          saveDraftMutation.isLoading ||
                          !favrRateResults
                        }
                      >
                        {t('saveRate')}
                      </Button>
                    </Col>
                  </Row>
                </Space>
              </Box>
            </Col>
          )}
        </Row>
      </Space>
    </PageContainer>
  );
};

export default withPageState(GenerateFavrRatesView, true);
